import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@stores';
import { hasActiveLenderBrokers } from '@utils';
import LenderList from '@components/widgets/LenderList';
import BrokerOrgBanner from '@components/widgets/BrokerOrgBanner';
import { ReactComponent as PlusIcon } from '@assets/icons/plus_icon.svg';
import { ReactComponent as AssetIcon } from '@assets/icons/asset_icon.svg';
import { ReactComponent as AgreementsIcon } from '@assets/icons/traffic_jam_icon.svg';
import WelcomeModal from '@components/widgets/WelcomeModal';
import BrokerPromoPanel from '@components/widgets/BrokerPromoPanel';
import { useRedirectNewBrokerToOnboarding } from '../hooks/useRedirectNewBrokerToOnboarding';
import { useBrokerPromo } from '../hooks/useBrokerPromo';

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function getGreeting(firstName: string) {
  const hour = new Date().getHours();
  if (hour < 12) return `Good morning, ${firstName}`;
  if (hour < 17) return `Good afternoon, ${firstName}`;
  return `Good evening, ${firstName}`;
}

function getDateString() {
  const d = new Date();
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const Dashboard = () => {
  const navigate = useNavigate();
  useRedirectNewBrokerToOnboarding();
  const { eligible: promoActive } = useBrokerPromo();
  const {
    user,
    isBroker,
    canAccessBrokerOrg,
    isInspectionEnabled,
    organisationConfig
  } = useAuthStore();

  const { hasAssetVerifications, hasBuyback } = organisationConfig;

  const [hasActiveLendersResult, setHasActiveLendersResult] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    (async () => {
      const result = await hasActiveLenderBrokers();
      setHasActiveLendersResult(result);
    })();
  }, []);

  const isNewBroker = isBroker() && hasActiveLendersResult === false;

  const greeting = isNewBroker
    ? `Welcome to Authentik8, ${user?.firstName}!`
    : getGreeting(user?.firstName || '');

  const scrollToLenders = () => {
    document
      .getElementById('lender-list')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="py-6 px-2">
      {promoActive ? (
        <BrokerPromoPanel onAddLenders={scrollToLenders} />
      ) : (
        /* Identity header */
        <div className="pb-5 mb-5 border-b border-grey-100">
          <h1 className="text-xl font-bold text-grey-900">{greeting}</h1>
          <p className="text-sm text-grey-500 mt-1">{getDateString()}</p>
        </div>
      )}

      {/* Action cards — only shown when inspection-enabled */}
      {isInspectionEnabled() && (
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            className="flex items-center gap-3 bg-primary text-white rounded-lg px-4 py-3 text-left hover:opacity-90 transition-opacity w-64"
            onClick={() => navigate('/inspections/create')}>
            <div
              className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.2)' }}>
              <PlusIcon className="text-white w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-bold leading-tight">New Inspection</p>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'rgba(255,255,255,0.75)' }}>
                Start a vehicle verification
              </p>
            </div>
          </button>

          <button
            className="flex items-center gap-3 bg-grey-50 border border-grey-200 rounded-lg px-4 py-3 text-left hover:bg-grey-100 transition-colors w-64"
            onClick={() => navigate('/inspections')}>
            <div className="w-9 h-9 rounded-md bg-grey-100 flex items-center justify-center flex-shrink-0">
              <img
                src="/images/inspections-icon.svg"
                width={20}
                height={20}
                alt=""
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-grey-900 leading-tight">
                Inspections
              </p>
              <p className="text-xs text-grey-500 mt-0.5">
                View and manage all
              </p>
            </div>
          </button>

          {hasAssetVerifications && (
            <button
              className="flex items-center gap-3 bg-grey-50 border border-grey-200 rounded-lg px-4 py-3 text-left hover:bg-grey-100 transition-colors"
              onClick={() => navigate('/verifications')}>
              <div className="w-9 h-9 rounded-md bg-grey-200 flex items-center justify-center flex-shrink-0">
                <AssetIcon className="w-4 h-4 [&_path]:stroke-[#8E8E93]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-grey-900 leading-tight">
                  Asset Verifications
                </p>
                <p className="text-xs text-grey-500 mt-0.5">
                  Quick VIN and rego checks
                </p>
              </div>
            </button>
          )}

          {hasBuyback && (
            <button
              className="flex items-center gap-3 bg-grey-50 border border-grey-200 rounded-lg px-4 py-3 text-left hover:bg-grey-100 transition-colors"
              onClick={() => navigate('/agreements')}>
              <div className="w-9 h-9 rounded-md bg-grey-200 flex items-center justify-center flex-shrink-0">
                <AgreementsIcon className="text-grey-600 w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-grey-900 leading-tight">
                  Agreements
                </p>
                <p className="text-xs text-grey-500 mt-0.5">
                  Buyback agreements
                </p>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Broker view: lender list + org panel */}
      {isBroker() && (
        <>
          {isInspectionEnabled() && (
            <p className="text-xs font-semibold uppercase tracking-wider text-grey-400 mb-3">
              Lender access
            </p>
          )}
          {isNewBroker && <WelcomeModal firstName={user?.firstName || ''} />}

          <div className="flex flex-col lg:flex-row gap-6 items-start">
            <div id="lender-list" className="flex-1 min-w-0">
              {isNewBroker && (
                <div
                  className="flex items-center justify-between gap-4 rounded-lg px-5 py-4 mb-4"
                  style={{ backgroundColor: '#033E73' }}>
                  <div>
                    <p className="text-base font-bold text-white">
                      You're almost ready!
                    </p>
                    <p
                      className="text-sm mt-1"
                      style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Request access to a lender below to start creating
                      inspections.
                    </p>
                  </div>
                  <span
                    className="text-2xl flex-shrink-0 animate-bounce"
                    style={{ color: 'rgba(255,255,255,0.6)' }}>
                    ↓
                  </span>
                </div>
              )}
              <LenderList className="!pt-0" />
            </div>
            {canAccessBrokerOrg() && (
              <div className="w-full lg:w-96 flex-shrink-0">
                <BrokerOrgBanner />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
